import { useMutation } from '@apollo/client/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import RequiredFieldsHint from '~/components/RequiredFieldsHint'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '~/components/ui/form'
import { PasswordInput } from '~/components/ui/password-input'
import { GQL_CHANGE_MY_PASSWORD } from '~/gql/account/account'
import { passwordRule } from '~/helpers/auth/password-rule'
import { tourAnchor } from '~/helpers/tour/anchors'
import { accountErrorKey } from '~/pages/account/account-errors'

const FIELDS = [
  { name: 'currentPassword', label: 'account.password.current', autoComplete: 'current-password' },
  { name: 'newPassword', label: 'account.password.new', autoComplete: 'new-password' },
  { name: 'confirmPassword', label: 'common.confirm-password', autoComplete: 'new-password' },
] as const

/**
 * Changement de mot de passe de l'utilisateur connecté (#77). L'API exige le
 * mot de passe actuel, révoque les autres sessions et garde celle-ci ouverte.
 * Un mot de passe actuel faux est signalé sur son champ, là où l'œil le cherche.
 */
function PasswordForm() {
  const { t } = useTranslation()
  const [changeMyPassword, { loading }] = useMutation(GQL_CHANGE_MY_PASSWORD)

  const schema = z.object({
    currentPassword: z.string().min(1, { message: t('input.password') }),
    newPassword: passwordRule(t('password.tooShort')),
    confirmPassword: z.string(),
  }).refine(values => values.newPassword === values.confirmPassword, {
    message: t('password.not-match.message'),
    path: ['confirmPassword'],
  })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
  })

  const onSubmit = async ({ currentPassword, newPassword }: Values) => {
    try {
      await changeMyPassword({ variables: { currentPassword, newPassword } })
      form.reset()
      toast.success(t('account.password.saved'))
    } catch (error) {
      const key = accountErrorKey(error)
      if (key === 'account.error.currentPassword') form.setError('currentPassword', { message: t(key) })
      else toast.error(t(key))
    }
  }

  return (
    <Card {...tourAnchor('account.password')}>
      <CardHeader>
        <CardTitle>{t('account.password.title')}</CardTitle>
        <CardDescription>{t('account.password.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <RequiredFieldsHint />
            <div className="grid gap-4 sm:grid-cols-3">
              {FIELDS.map(({ name, label, autoComplete }) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t(label)}</FormLabel>
                      <FormControl><PasswordInput aria-required autoComplete={autoComplete} {...field} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <Button type="submit" disabled={loading} className="self-start">
              {loading && <Loader className="mr-2 size-4 animate-spin" />}
              {t('account.password.submit')}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

export default PasswordForm
